<script lang="ts" setup>
import type { ImageEditorCropMode } from '../../runtime/imageEditor';
import type {
    AspectRatioOption,
    CropperTemplate
} from '../../runtime/cropper';
import CropperRange from './CropperRange.vue';

defineProps<{
    templates: CropperTemplate[];
    aspectRatioOptions: AspectRatioOption[];
    canSaveCustomAspectRatio: boolean;
}>();

const emit = defineEmits<{
    (e: 'set-crop-mode', value: ImageEditorCropMode): void;
    (e: 'reset-selection'): void;
    (e: 'apply-custom-aspect-ratio', save?: boolean): void;
    (e: 'apply-template', value: CropperTemplate): void;
    (e: 'rotate-image', degrees: number): void;
    (e: 'apply-transform', label: string): void;
    (e: 'save-history', label: string): void;
}>();

const { $t } = useLocalization();
const fieldId = useId();

const cropMode = defineModel<ImageEditorCropMode>('cropMode', { required: true });
const aspectRatio = defineModel<string>('aspectRatio', { required: true });
const fixedWidth = defineModel<number>('fixedWidth', { required: true });
const fixedHeight = defineModel<number>('fixedHeight', { required: true });
const circularSize = defineModel<number>('circularSize', { required: true });
const customRatioWidth = defineModel<number>('customRatioWidth', { required: true });
const customRatioHeight = defineModel<number>('customRatioHeight', { required: true });
const flipHorizontal = defineModel<boolean>('flipHorizontal', { required: true });
const flipVertical = defineModel<boolean>('flipVertical', { required: true });
const scale = defineModel<number>('scale', { required: true });

function selectFreeCrop()
{
    cropMode.value = 'free';
    emit('set-crop-mode', 'free');
}

function selectAspectRatio(value: string)
{
    aspectRatio.value = value;
    cropMode.value = 'fixed-ratio';
    emit('set-crop-mode', 'fixed-ratio');
}
</script>

<template>
    <div class="flex min-w-0 flex-col gap-[16px]">
        <EditoPageSurface title="[[[Kadrowanie]]]">
            <div class="mb-[10px] grid grid-cols-3 gap-[6px]">
                <IdeoAction
                    type="button"
                    size="xs"
                    :variant="cropMode === 'free' ? 'primary' : 'secondary'"
                    :aria-pressed="cropMode === 'free'"
                    class="w-full justify-center"
                    @click="selectFreeCrop"
                >
                    {{ $t('[[[Dowolne]]]') }}
                </IdeoAction>
                <IdeoAction
                    v-for="option in aspectRatioOptions"
                    :key="`chip.${option.value}.${option.text}`"
                    type="button"
                    size="xs"
                    :variant="cropMode === 'fixed-ratio' && aspectRatio === option.value ? 'primary' : 'secondary'"
                    :aria-pressed="cropMode === 'fixed-ratio' && aspectRatio === option.value"
                    class="w-full justify-center"
                    @click="selectAspectRatio(option.value)"
                >
                    {{ option.short ?? option.text }}
                </IdeoAction>
            </div>
            <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="$t('[[[Tryb kadrowania]]]')" :label-for="fieldId + '-cropMode'"><IdeoFormSelect :id="fieldId + '-cropMode'" v-model="cropMode" :aria-label="$t('[[[Tryb kadrowania]]]')" @change="emit('set-crop-mode', $event)">
                <IdeoFormSelectOption value="none">
                    {{ $t('[[[Bez przycinania]]]') }}
                </IdeoFormSelectOption>
                <IdeoFormSelectOption value="free">
                    {{ $t('[[[Dowolne]]]') }}
                </IdeoFormSelectOption>
                <IdeoFormSelectOption value="fixed-ratio">
                    {{ $t('[[[Proporcja]]]') }}
                </IdeoFormSelectOption>
                <IdeoFormSelectOption value="fixed-size">
                    {{ $t('[[[Stały rozmiar kadru]]]') }}
                </IdeoFormSelectOption>
                <IdeoFormSelectOption value="circular">
                    {{ $t('[[[Koło]]]') }}
                </IdeoFormSelectOption>
                <IdeoFormSelectOption value="template">
                    {{ $t('[[[Szablon CMS]]]') }}
                </IdeoFormSelectOption>
            </IdeoFormSelect></IdeoFormGroup>

            <IdeoFormGroup v-if="cropMode === 'fixed-ratio'" nospace class="mt-[12px] first:mt-0" :label="$t('[[[Proporcje kadru]]]')" :label-for="fieldId + '-aspectRatio'"><IdeoFormSelect :id="fieldId + '-aspectRatio'" v-model="aspectRatio" :aria-label="$t('[[[Proporcje kadru]]]')" @change="emit('reset-selection')">
                <IdeoFormSelectOption v-for="item in aspectRatioOptions" :key="`${item.value}.${item.text}`" :value="item.value">
                    {{ $t(item.text) }}
                </IdeoFormSelectOption>
            </IdeoFormSelect></IdeoFormGroup>
            <div v-if="cropMode === 'fixed-ratio'" class="editor-ratio-custom mt-[8px]">
                <div class="grid gap-[12px]">
                    <IdeoFormGroup nospace :label="$t('[[[Szerokość proporcji]]]')" :label-for="fieldId + '-customRatioWidth'"><IdeoFormNumber :id="fieldId + '-customRatioWidth'"
                        v-model="customRatioWidth" :aria-label="$t('[[[Szerokość proporcji]]]')"
                        :min="1"
                        :max-fraction-digits="0"
                        :placeholder="$t('[[[Szerokość]]]')"
                        @input="emit('apply-custom-aspect-ratio')"
                    /></IdeoFormGroup>
                    <IdeoFormGroup nospace :label="$t('[[[Wysokość proporcji]]]')" :label-for="fieldId + '-customRatioHeight'"><IdeoFormNumber :id="fieldId + '-customRatioHeight'"
                        v-model="customRatioHeight" :aria-label="$t('[[[Wysokość proporcji]]]')"
                        :min="1"
                        :max-fraction-digits="0"
                        :placeholder="$t('[[[Wysokość]]]')"
                        @input="emit('apply-custom-aspect-ratio')"
                    /></IdeoFormGroup>
                </div>
                <IdeoAction stop-propagation
                    variant="secondary"
                    class="mt-[8px] w-full"
                    :disabled="!canSaveCustomAspectRatio"
                    :text="$t('[[[Dodaj własną proporcję]]]')"
                    @click="emit('apply-custom-aspect-ratio', true)"
                />
            </div>

            <div v-if="cropMode === 'fixed-size'" class="mt-[12px] grid gap-[12px]">
                <IdeoFormGroup nospace :label="$t('[[[Szerokość kadru]]]')" :label-for="fieldId + '-fixedWidth'"><IdeoFormNumber :id="fieldId + '-fixedWidth'" v-model="fixedWidth" suffix="px" :aria-label="$t('[[[Szerokość kadru]]]')" :min="1" :max-fraction-digits="0" @input="emit('set-crop-mode', 'fixed-size')" /></IdeoFormGroup>
                <IdeoFormGroup nospace :label="$t('[[[Wysokość kadru]]]')" :label-for="fieldId + '-fixedHeight'"><IdeoFormNumber :id="fieldId + '-fixedHeight'" v-model="fixedHeight" suffix="px" :aria-label="$t('[[[Wysokość kadru]]]')" :min="1" :max-fraction-digits="0" @input="emit('set-crop-mode', 'fixed-size')" /></IdeoFormGroup>
            </div>

            <IdeoFormGroup v-if="cropMode === 'circular'" nospace class="mt-[12px] first:mt-0" :label="$t('[[[Średnica kadru]]]')" :label-for="fieldId + '-circularSize'"><IdeoFormNumber :id="fieldId + '-circularSize'"
                v-model="circularSize" suffix="px" :aria-label="$t('[[[Średnica kadru]]]')"

                :min="32"
                :max="2048"
                :max-fraction-digits="0"
                @input="emit('set-crop-mode', 'circular')"
            /></IdeoFormGroup>

            <div v-if="cropMode === 'template'" class="editor-template-list grid gap-[6px] mt-[8px]">
                <IdeoAction
                    v-for="template in templates"
                    :key="template.name"
                    type="button"
                    variant="secondary"
                    size="xs"
                    class="w-full justify-between text-start"
                    @click="emit('apply-template', template)"
                >
                    {{ template.name }} <span>{{ template.width }}×{{ template.height }}</span>
                </IdeoAction>
            </div>
        </EditoPageSurface>

        <EditoPageSurface title="[[[Transformacje]]]">
            <div class="grid grid-cols-2 gap-[8px]">
                <IdeoAction stop-propagation
                    variant="secondary"
                    icon="rotate-ccw" :text="$t('[[[W lewo]]]')"
                    @click="emit('rotate-image', -90)"
                />
                <IdeoAction stop-propagation
                    variant="secondary"
                    icon="rotate-cw" :text="$t('[[[W prawo]]]')"
                    @click="emit('rotate-image', 90)"
                />
                <IdeoAction stop-propagation
                    :variant="flipHorizontal ? 'primary' : 'secondary'" :aria-pressed="flipHorizontal"
                    :text="$t('[[[Odbij X]]]')"
                    @click="flipHorizontal = !flipHorizontal; emit('apply-transform', $t('[[[Odbicie poziome]]]'))"
                />
                <IdeoAction stop-propagation
                    :variant="flipVertical ? 'primary' : 'secondary'" :aria-pressed="flipVertical"
                    :text="$t('[[[Odbij Y]]]')"
                    @click="flipVertical = !flipVertical; emit('apply-transform', $t('[[[Odbicie pionowe]]]'))"
                />
            </div>
            <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${$t('[[[Skala]]]')}: ${scale}%`" :label-for="fieldId + '-scale'">
                <CropperRange :id="fieldId + '-scale'"
                v-model="scale" :aria-label="$t('[[[Skala obrazu]]]')"
                :min="10"
                :max="300"
                :step="5"
                @change="emit('save-history', $t('[[[Zmiana skali]]]'))"
            />
            </IdeoFormGroup>
        </EditoPageSurface>

        <EditoPageSurface title="[[[Filtry]]]">
            <slot name="filters" />
        </EditoPageSurface>
    </div>
</template>
